<template>
  <div>
    <div v-if="showUpsell" class="row justify-center q-col-gutter-lg">
      <div class="col-12 col-md-6 col-lg-4">
        <q-img
          src="https://storage.googleapis.com/hello-data-ai-public-assets/expense-benchmark-screenshot.png"
          height="500px"
          fit="contain"
        />
      </div>
      <div class="col-12 col-md-6 col-lg-4">
        <dashboard-card
          icon="bolt"
          title="Financial Analysis"
          subtitle="AI-powered financial analysis on any property."
          color="primary"
          shadow
          style="max-width: 416px; width: 100%"
        >
          <div class="q-mb-xl">
            <div v-if="billedPerSeat" class="flex items-end">
              <div class="text-bold" style="font-size: 2.4rem; line-height: 1">$200</div>
              <div
                class="q-ml-xs q-mb-xs"
                :class="[$q.dark.isActive ? 'text-grey-4' : 'text-grey-8']"
                style="line-height: 1"
              >
                /user/month
              </div>
            </div>
            <div v-else class="flex items-end">
              <div class="text-bold" style="font-size: 2.4rem; line-height: 1">$0.20</div>
              <div
                class="q-ml-xs q-mb-xs"
                :class="[$q.dark.isActive ? 'text-grey-4' : 'text-grey-8']"
                style="line-height: 1"
              >
                /unit/month
              </div>
            </div>
            <div
              class="q-mt-md"
              :class="[$q.dark.isActive ? 'text-grey-3' : 'text-grey-9']"
              style="height: 80px"
            >
              Instantly analyze multifamily investments or benchmark expenses in any market with
              only a property name or address.
            </div>
            <div class="q-my-md q-gutter-xs">
              <div v-for="(f, index) in features" :key="index">
                <q-icon name="task_alt" class="q-mr-sm" color="green-5" size="20px" />
                <span>{{ f.label }}</span>
              </div>
            </div>
          </div>
          <template #footer>
            <q-btn
              v-if="hasPropertyReports"
              id="intercom-action"
              color="primary"
              no-caps
              unelevated
              class="full-width"
              label="Review Add-On"
              :to="{ name: 'Subscribe to Financial Analysis' }"
            />
            <q-btn
              v-else
              id="intercom-action"
              color="primary"
              no-caps
              unelevated
              class="full-width"
              label="Subscribe to Property Reports"
              :to="{ name: 'Subscribe to Property Reports' }"
            />
          </template>
        </dashboard-card>
      </div>
    </div>
    <property-report-card
      v-else
      title="Financial Analysis"
      description="Model stabilized income and expenses over the next 12 months based on operating metrics from similar assets."
      :printing-mode="printingMode"
      :trial-days-left="financialAnalysisTrialDaysLeft"
      :trial-end="financialAnalysisTrialEnd ?? undefined"
    >
      <template #actions>
        <q-chip
          v-if="financialAnalysisTrialDaysLeft > 0"
          :ripple="false"
          class="text-primary"
          outline
        >
          <div class="text-center full-width">
            Trial ends in<b class="q-ml-xs">{{ financialAnalysisTrialDaysLeft }} days</b>
          </div>
          <q-tooltip> Trial ends on {{ formatDate(financialAnalysisTrialEnd) }} </q-tooltip>
        </q-chip>
      </template>
      <q-separator />
      <q-markup-table
        v-if="isLoading(expenseBenchmarks)"
        id="intercom-financial-analysis-content"
        dense
        style="border-bottom: 1px solid rgba(0, 0, 0, 0.12)"
      >
        <tbody>
          <tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <th v-for="n in 4" scope="col" :key="n">
              <q-skeleton animation="blink" type="text" />
            </th>
          </tr>
          <tr v-for="n in 5" :key="n">
            <td v-for="t in 4" :key="t">
              <q-skeleton animation="blink" type="text" />
            </td>
          </tr>
          <tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <td colspan="3">
              <q-skeleton animation="blink" type="text" />
            </td>
            <td>
              <q-skeleton animation="blink" type="text" />
            </td>
          </tr>
          <tr v-for="n in 11" :key="n">
            <td v-for="t in 4" :key="t">
              <q-skeleton animation="blink" type="text" />
            </td>
          </tr>
          <tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <td colspan="3">
              <q-skeleton animation="blink" type="text" />
            </td>
            <td>
              <q-skeleton animation="blink" type="text" />
            </td>
          </tr>
          <tr v-for="n in 1" :key="n">
            <td v-for="t in 4" :key="t">
              <q-skeleton animation="blink" type="text" />
            </td>
          </tr>
        </tbody>
      </q-markup-table>
      <q-table
        v-else
        id="intercom-financial-analysis-content"
        :rows="rows"
        :columns="columns"
        :rows-per-page-options="[0]"
        hide-bottom
        dense
        style="min-height: 500px"
      >
        <template #header="props">
          <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
            <q-th align="left">Income</q-th>
            <q-th align="right">
              {{ subjectDetail?.building_name || subjectDetail?.street_address }}
            </q-th>
            <q-th align="right">Per Unit</q-th>
            <q-th align="right">Per Unit / Month</q-th>
            <q-th align="right">% Of EGI</q-th>
          </q-tr>
        </template>
        <template #body="props">
          <div v-if="isLoading(expenseBenchmarks)" style="height: 200px">
            <q-inner-loading showing />
          </div>
          <template v-else-if="isLoaded(expenseBenchmarks)">
            <q-tr
              v-if="[5, 16].includes(props.rowIndex)"
              :props="props"
              :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
            >
              <q-td colspan="4">
                <div v-if="props.rowIndex === 5">Operating Expenses</div>
                <div v-if="props.rowIndex === 16">NOI</div>
              </q-td>
              <q-td class="text-right">% Of EGI</q-td>
            </q-tr>
            <q-tr :props="props" :class="{ 'text-bold': [0, 4, 15, 16].includes(props.rowIndex) }">
              <q-td v-for="(col, colIndex) in props.cols" :key="col.name" :props="props">
                <div
                  :class="{
                    'q-ml-sm': colIndex === 0 && ![0, 4, 15, 16].includes(props.rowIndex),
                    'cursor-pointer':
                      colIndex === 1 && props.row.key in expenseBenchmarks.data.subject_estimate,
                    'row justify-end': colIndex === 1 && props.rowIndex === 0
                  }"
                >
                  <q-btn
                    v-if="!gprEditMode && colIndex === 1 && props.rowIndex === 0 && !printingMode"
                    flat
                    dense
                    size="sm"
                    class="float-left q-mr-sm"
                    @click="goToGPREditMode"
                  >
                    <q-icon name="edit" class="text-grey-7" />
                    <q-tooltip> Edit GPR to see how it impacts the financial analysis </q-tooltip>
                  </q-btn>
                  <div v-if="gprEditMode && colIndex === 1 && props.rowIndex === 0">
                    <q-input
                      v-model.number="customGPR"
                      type="number"
                      dense
                      outlined
                      style="width: 150px"
                      @keyup.enter="saveCustomGPR"
                    />
                  </div>
                  <div v-else class="q-my-auto">
                    <q-spinner
                      class="q-ml-sm"
                      v-if="
                        isLoading(subjectTaxHistory) &&
                        ['real_estate_taxes', 'total_operating_expenses', 'noi'].indexOf(
                          props.row.key
                        ) > -1 &&
                        colIndex > 0
                      "
                    />
                    <template v-else>
                      {{ col.value }}
                    </template>
                    <q-menu
                      v-if="
                        colIndex === 1 && props.row.key in expenseBenchmarks.data.subject_estimate
                      "
                      @show="$tracker.openedPopup('expense_benchmarks_explanation')"
                    >
                      <div class="text-bold q-pa-sm text-center">
                        {{ props.row.label }}
                      </div>
                      <q-markup-table flat dense square bordered>
                        <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
                          <tr class="text-left">
                            <th scope="col" class="text-left">Lower</th>
                            <th scope="col" class="text-right">Average</th>
                            <th scope="col" class="text-right">Upper</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td class="text-left">
                              {{
                                expenseBenchmarks.data.subject_estimate[props.row.key].lower !==
                                null
                                  ? formatMoney(
                                      expenseBenchmarks.data.subject_estimate[props.row.key].lower
                                    )
                                  : 'N/A'
                              }}
                            </td>
                            <td class="text-right">
                              {{
                                formatMoney(
                                  expenseBenchmarks.data.subject_estimate[props.row.key].predicted
                                )
                              }}
                            </td>
                            <td class="text-right">
                              {{
                                expenseBenchmarks.data.subject_estimate[props.row.key].upper !==
                                null
                                  ? formatMoney(
                                      expenseBenchmarks.data.subject_estimate[props.row.key].upper
                                    )
                                  : 'N/A'
                              }}
                            </td>
                          </tr>
                        </tbody>
                      </q-markup-table>
                      <template v-if="formattedExplanations[props.row.key]">
                        <div class="text-bold q-pa-sm text-center">
                          Most Important Factors
                          <q-icon name="help" class="q-ml-sm" />
                          <q-tooltip>
                            <div class="text-left">
                              The following factors are the most important ones that have been used
                              to compute most similar properties and estimate the subject property's
                              financials.
                            </div>
                          </q-tooltip>
                        </div>
                        <q-markup-table dense flat bordered>
                          <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
                            <tr>
                              <th scope="col">Dimension</th>
                              <th scope="col">Value</th>
                              <th scope="col">Impact</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="val in formattedExplanations[props.row.key]" :key="val.key">
                              <td>{{ val.key }}</td>
                              <td class="text-right">{{ val.value }}</td>
                              <td>
                                <div
                                  :class="{
                                    'text-green':
                                      (val.higherBetter && val.impact > 0) ||
                                      (!val.higherBetter && val.impact < 0),
                                    'text-red':
                                      (val.higherBetter && val.impact < 0) ||
                                      (!val.higherBetter && val.impact > 0)
                                  }"
                                >
                                  <div v-if="val.impactPercent > 0.1">⬆⬆⬆</div>
                                  <div v-else-if="val.impactPercent > 0.05">⬆⬆</div>
                                  <div v-else-if="val.impactPercent > 0.0">⬆</div>
                                  <div v-else-if="val.impactPercent < -0.1">⬇⬇⬇</div>
                                  <div v-else-if="val.impactPercent < -0.05">⬇⬇</div>
                                  <div v-else-if="val.impactPercent < 0.0">⬇</div>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </q-markup-table>
                      </template>
                    </q-menu>
                  </div>
                </div>
              </q-td>
            </q-tr>
          </template>
        </template>
        <template #loading>
          <q-inner-loading showing />
        </template>
        <template #no-data>
          <div class="text-center full-width q-ma-md">
            Subject Property With # Units and Unit Mix Required
          </div>
        </template>
      </q-table>
      <q-separator />
      <q-card-section>
        <div class="text-caption">
          Computed based on operating expense data from similar multifamily properties in your
          market, controlling for differences in size, age, quality, amenities and location.
        </div>
      </q-card-section>
    </property-report-card>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QTableColumn } from 'quasar'
import type { PropertyDetailsResponse, ExpenseBenchmarksResponse } from 'common'
import { formatMoney, formatPercent } from '@/utils'
import { isLoaded, isLoading, isNotStarted } from '@/utils/loading'
import { usePropertyReportStore } from '@/stores/propertyreport'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import DashboardCard from '@/components/DashboardCard.vue'
import { useStripeStore } from '@/stores/stripe'
import { formatDate } from '@/utils'

interface Explanation {
  key: string
  value: string | number
  impact: number
  impactPercent: number
  higherBetter: boolean
}

export default {
  name: 'PropertyCompareFinancialAnalysis',
  components: { PropertyReportCard, DashboardCard },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    printingMode: {
      type: Boolean,
      default: false
    },
    showUpsell: {
      type: Boolean,
      default: false
    }
  },
  async created() {
    if (this.subjectDetail && isNotStarted(this.expenseBenchmarks)) {
      await usePropertyReportStore().getExpenseBenchmarks(
        this.subjectDetail,
        this.customGPR || undefined
      )
    }
  },
  data() {
    return {
      formatDate,
      formatMoney,
      formatPercent,
      gprEditMode: false,
      loading: false,
      customGPR: null as number | null,
      features: [
        {
          label: 'Generates Full Year 1 Financial Analysis.'
        },
        {
          label: 'Benchmarks Operating Expenses.'
        },
        {
          label: 'Models NOI with <10% median error.'
        },
        {
          label: 'Full Pro Forma & Waterfall Model Export.'
        },
        {
          label: 'No Set Up or Data Entry Required.'
        }
      ],
      columns: [
        { name: 'label', field: 'label', align: 'left' },
        {
          name: 'subject',
          field: 'subject_estimate',
          align: 'right',
          format: (_, row) => {
            return formatMoney(Math.round(row.subject_estimate) || null)
          }
        },
        {
          name: 'per_unit',
          field: 'per_unit',
          align: 'right',
          format: (_, row) => {
            return formatMoney(Math.round(row.per_unit) || null)
          }
        },
        {
          name: 'per_unit_per_month',
          field: 'per_unit_per_month',
          align: 'right',
          format: (_, row) => {
            return formatMoney(Math.round(row.per_unit_per_month) || null)
          }
        },
        {
          name: 'percent',
          field: 'percent',
          align: 'right',
          format: (_, row) => {
            return formatPercent(row.percent)
          }
        }
      ] as QTableColumn[]
    }
  },
  computed: {
    hasPropertyReports() {
      return useStripeStore().hasPropertyReports
    },
    financialAnalysisTrialEnd(): string | null {
      const sub = useStripeStore().subscriptions.find((_) =>
        _.products.includes(import.meta.env.VITE_STRIPE_FINANCIAL_ANALYSIS_PRODUCT)
      )
      return sub?.trial_end || null
    },
    financialAnalysisTrialDaysLeft(): number {
      return useStripeStore().financialAnalysisTrialDaysLeft || 0
    },
    expenseBenchmarks() {
      return usePropertyReportStore().expenseBenchmark
    },
    subjectTaxHistory() {
      return usePropertyReportStore().subjectTaxHistory
    },
    billedPerSeat() {
      return useStripeStore().billedPerSeat
    },
    formattedExplanations(): Record<string, Explanation[]> {
      if (!isLoaded(this.expenseBenchmarks)) {
        return {}
      }

      const higherBetter = ['gross_potential_rent', 'other_income', 'parking_income', 'egi', 'noi']

      const results: Record<string, Explanation[]> = {}

      for (const [key, val] of Object.entries(this.expenseBenchmarks.data.subject_estimate)) {
        if (val && val.explanation) {
          results[key] = Object.entries(val.explanation)
            .filter(([, v]) => Math.abs(v.impact) >= 2.5)
            .map(([k, v]) => ({
              key: this.humanizeKey(k),
              value: this.humanizeValue(v.value, k),
              impact: v.impact,
              impactPercent: val.predicted ? v.impact / val.predicted : 0,
              higherBetter: higherBetter.includes(key)
            }))
            .sort((a, b) =>
              higherBetter.includes(key) ? b.impact - a.impact : a.impact - b.impact
            )
        }
      }
      return results
    },
    rows() {
      if (!isLoaded(this.expenseBenchmarks)) {
        return []
      }

      return [
        { label: 'Gross Potential Rent (GPR)', key: 'gross_potential_rent', type: 'income' },
        { label: 'Vacancy Loss', key: 'vacancy_loss', type: 'income' },
        { label: 'Other Income', key: 'other_income', type: 'income' },
        { label: 'Parking Income', key: 'parking_income', type: 'income' },
        { label: 'Effective Gross Income (EGI)', key: 'egi', type: 'income' },
        { label: 'Real Estate Taxes', key: 'real_estate_taxes', type: 'expense' },
        { label: 'Property Insurance', key: 'property_insurance', type: 'expense' },
        { label: 'Utilities', key: 'utilities', type: 'expense' },
        { label: 'Repair and Maintenance', key: 'repair_and_maintenance', type: 'expense' },
        { label: 'Management Fees', key: 'management_fees', type: 'expense' },
        { label: 'Payroll and Benefits', key: 'payroll_and_benefits', type: 'expense' },
        { label: 'Marketing', key: 'advertising_and_marketing', type: 'expense' },
        { label: 'Professional Fees', key: 'professional_fees', type: 'expense' },
        {
          label: 'General and Administrative',
          key: 'general_and_administrative',
          type: 'expense'
        },
        { label: 'Other Expenses', key: 'other_expenses', type: 'expense' },
        { label: 'Total Operating Expenses', key: 'total_operating_expenses', type: 'expense' },
        { label: 'Net Operating Income (NOI)', key: 'noi', type: 'expense' }
      ].map((_) => ({
        label: _.label,
        key: _.key,
        subject_estimate: this.subjectEstimate(_.key, 'total'),
        per_unit: this.subjectEstimate(_.key, 'per_unit'),
        per_unit_per_month: this.subjectEstimate(_.key, 'per_unit_per_month'),
        percent: this.subjectEstimate(_.key, _.type === 'income' ? 'percent_gpr' : 'percent_egi')
      }))
    }
  },
  watch: {
    subjectDetail() {
      if (this.subjectDetail) {
        usePropertyReportStore()
          .getExpenseBenchmarks(this.subjectDetail, this.customGPR || undefined)
          .catch(console.error)
      }
    }
  },
  methods: {
    isLoaded,
    isLoading,
    goToGPREditMode() {
      if (!isLoaded(this.expenseBenchmarks)) {
        return
      }
      this.customGPR =
        this.expenseBenchmarks.data.subject_estimate.gross_potential_rent?.predicted || null
      this.gprEditMode = true
    },
    async saveCustomGPR() {
      if (!isLoaded(this.expenseBenchmarks)) {
        return
      }
      this.loading = true
      this.gprEditMode = false
      if (this.subjectDetail) {
        await usePropertyReportStore().getExpenseBenchmarks(
          this.subjectDetail,
          this.customGPR || undefined
        )
        this.$tracker.changedGPR(
          this.subjectDetail!.id,
          this.expenseBenchmarks.data?.subject_estimate.gross_potential_rent?.predicted || null,
          this.customGPR || null
        )
      }
    },
    humanizeKey(str: string) {
      return str
        .replace(/_/g, ' ')
        .replace(/(?:^|\s)\S/g, (a) => a.toUpperCase())
        .replace('Gpr', 'GPR')
        .replace('Egi', 'EGI')
    },
    humanizeValue(val: number | boolean | string | null, key: string) {
      if (typeof val === 'boolean') {
        return val ? '✔' : '✘'
      } else if (typeof val === 'number') {
        if (key === 'building_quality') {
          return `${Math.round(val * 100) / 10}/10`
        } else if (key === 'gpr_per_unit') {
          return formatMoney(val, 0)
        }
        return Math.round(val)
      } else if (val === null) {
        return 'N/A'
      }
      return val
    },
    subjectEstimate(
      key: keyof ExpenseBenchmarksResponse['subject_estimate'],
      type: 'total' | 'per_unit' | 'percent_egi' | 'percent_gpr' | 'per_unit_per_month'
    ): number | null {
      if (!isLoaded(this.expenseBenchmarks)) {
        return null
      }
      if (
        key in this.expenseBenchmarks.data.subject_estimate &&
        this.expenseBenchmarks.data.subject_estimate[key] !== null
      ) {
        const value = this.expenseBenchmarks.data.subject_estimate[key].predicted
        if (type === 'per_unit') {
          return this.subjectDetail?.number_units
            ? (value || 0) / this.subjectDetail.number_units
            : null
        } else if (type === 'percent_gpr') {
          const gpr = this.expenseBenchmarks.data.subject_estimate.gross_potential_rent?.predicted
          return gpr ? value / gpr : null
        } else if (type === 'percent_egi') {
          const egi = this.expenseBenchmarks.data.subject_estimate.egi?.predicted
          return egi ? value / egi : null
        } else if (type === 'per_unit_per_month') {
          return this.subjectDetail?.number_units
            ? (value || 0) / this.subjectDetail.number_units / 12.0
            : null
        }
        return value ? Math.round((value || 0) / 100) * 100 : null
      }
      return null
    }
  }
}
</script>
